import _ from 'lodash'

export default class Serializer {
  toDotKey (key) {
    return _.chain(key).castArray().filter(k => _.isString(k) || _.isNumber(k)).join('.').replace(/\.([0-9])+(\.?|$)/g, '[$1]$2').value()
  }

  trim (formula) {
    if (formula) {
      if (_.includes(formula, ' || ') || _.includes(formula, ' && ')) {
        return formula
      }

      return _.replace(formula, /^\((.+)\)$/, '$1')
    }
  }

  serialize (formula) {
    if (_.isArray(formula)) {
      return this.serialize({
        '&&': formula
      })
    }

    if (_.isPlainObject(formula)) {
      const operator = _.chain(formula).keys().first().value()
      const operands = _.chain(formula).get(operator).map(this.serialize.bind(this)).reject(s => _.isEmpty(_.toString(s))).value()
      const f = _.join(operands, ` ${operator} `)

      if (_.size(operands) > 1) {
        return `(${f})`
      }

      return f
    }

    return formula
  }

  serializeNode (node) {
    switch (node?.type) {
      case 'Literal':
        return this.serializeResult(node.value, null, node?.json)
      case 'Identifier':
        return node.name
      case 'Task':
        return _.chain([node.task, node.name]).compact().join('::').castArray().concat(this.serializeNode(node.taskArg)).compact().join('(').castArray().concat(node.taskArg && ')').compact().join('').value()
      case 'Vrn':
        return node.vrn
      case 'Annotation':
        return `#${node.annotation}:${this.serializeNode(node.expression)}`
      case 'Property':
        return `${this.serializeNode(node.key)}: ${this.serializeNode(node.value)}`
      case 'Compound':
        return _.chain(node.body).map(this.serializeNode.bind(this)).join(' ').value()
      case 'SequenceExpression':
        return _.chain(node.expressions).map(this.serializeNode.bind(this)).join(' ').value()
      case 'MemberExpression':
        return node.computed ? `${this.serializeNode(node.object)}[${this.serializeNode(node.property)}]` : `${this.serializeNode(node.object)}.${this.serializeNode(node.property)}`
      case 'UnaryExpression':
        return `${node.operator}${this.serializeNode(node.argument)}`
      case 'LogicalExpression':
      case 'BinaryExpression':
        return `(${this.serializeNode(node.left)} ${node.operator} ${this.serializeNode(node.right)})`
      case 'ConditionalExpression':
        return `(${this.serializeNode(node.test)} ? ${this.serializeNode(node.consequent)} : ${this.serializeNode(node.alternate)})`
      case 'CallExpression':
        return `${this.serializeNode(node.callee)}(${_.chain(node.arguments).map(this.serializeNode.bind(this)).join(', ').value()})`
      case 'ArrayExpression':
        return node.json ? this.serializeResult(this.jsonNode(node), null, true) : `[${_.chain(node.elements).map(this.serializeNode.bind(this)).join(', ').value()}]`
      case 'ObjectExpression':
        return node.json ? this.serializeResult(this.jsonNode(node), null, true) : `{${_.chain(node.properties).map(this.serializeNode.bind(this)).join(', ').value()}}`
      case 'TemplateLiteral':
        return `\`${_.chain(node.quasis).map((n, i) => n?.tail ? this.serializeNode(n) : `${this.serializeNode(n)}\${${this.serializeNode(node?.expressions[i])}}`).join('').value()}\``
      case 'TemplateElement':
        return node.value.cooked || node.value.raw
    }
  }

  jsonNode (node) {
    switch (node?.type) {
      case 'Literal':
        return node.value
      case 'ObjectExpression':
        return _.reduce(node.properties, (r, n) => _.set(r, n.key.name || n.key.value, this.jsonNode(n.value)), {})
      case 'ArrayExpression':
        return _.map(node.elements, this.jsonNode.bind(this))
    }

    return this.serializeNode(node)
  }

  serializeResult (v, k = undefined, json = false) {
    if ((_.isPlainObject(v) || _.isArray(v)) && json) {
      return JSON.stringify(v)
    }

    if (_.isNil(v) && _.isNil(k)) {
      return v === null ? 'null' : 'undefined'
    }

    if (_.isPlainObject(v)) {
      return `{${_.chain(v).map((vv, kk) => this.serializeResult(vv, kk, json)).join(', ').value()}}`
    }

    if (_.isArray(v)) {
      return `[${_.chain(v).map((vv, kk) => this.serializeResult(vv, kk, json)).join(', ').value()}]`
    }

    if (_.startsWith(v, '{') || _.startsWith(v, '[')) {
      return v
    }

    if (k && _.isString(k)) {
      return `${k}: ${this.serializeResult(v, null, json)}`
    }

    return _.isString(v) ? `'${v}'` : v
  }
}
